import { useEffect, useState } from "react";
import { Steps } from "antd";
import CompanyInfo from "./components/CompanyInfo";
import DirectorsShareholdersInfo from "./components/DirectorsShareholdersInfo";
import RepresentativeInfo from "./components/RepresentativeInfo";
import BeneficiaryInfo from "./components/BeneficiaryInfo";
import FinancialInfo from "./components/FinancialInfo";
import CorporateDeclaration from "./components/CorporateDeclaration";
import UploadDocuments from "./components/UploadDocuments";
import ApplicationStatus from "./components/ApplicationStatus";
import { getCorporateAccount } from "../../../api/request/corporate";
import Loader from "../../../components/Loader";

const { Step } = Steps;

const OnBoardingSteps = ({
  userId,
  step,
  isReviewMode = false,
  reviewStatus = "Pending",
  onReviewCompleted = () => {},
  canUpdate = true,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [data, setData] = useState(null);
  const [completedStep, setCompletedStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const isFormLocked = isReviewMode || !canUpdate;
  const isSubmitted = isFormLocked;

  const resolveStepFromAccount = (accountData) => {
    const reportedStep = Number(accountData?.currentStep);
    let step =
      Number.isFinite(reportedStep) && reportedStep > 0 ? reportedStep : 1;
    const status = String(accountData?.status || "").toLowerCase();

    if (status === "approved" || Number(accountData?.completedPercentage) >= 100) {
      step = 8;
    }

    if (step > 8) step = 8;
    return step;
  };

  const syncAccountProgress = async ({
    showLoader = false,
    preserveCurrentStep = false,
  } = {}) => {
    if (showLoader) setIsLoading(true);
    try {
      const { data } = await getCorporateAccount(
        new URLSearchParams({ userId: userId }).toString()
      );
      if (data?.success) {
        const accountData = data?.data;
        const resolvedStep = resolveStepFromAccount(accountData);
        setData(accountData);

        if (!preserveCurrentStep) {
          setCurrentStep(resolvedStep);
          setCompletedStep(resolvedStep >= 8 ? 7 : resolvedStep - 1);
        } else {
          setCompletedStep((prev) =>
            Math.max(prev, resolvedStep >= 8 ? 7 : resolvedStep - 1)
          );
        }
      }
    } catch (error) {
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId && (isReviewMode || Number(step) === 10)) {
      syncAccountProgress({ showLoader: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, step, isReviewMode]);

  const onNext = () => {
    setCurrentStep((prev) => {
      setCompletedStep((completed) => Math.max(completed, prev));
      return prev + 1;
    });
    syncAccountProgress({ preserveCurrentStep: true });
  };

  const handleDocumentsSubmitted = () => {
    setCompletedStep((prev) => Math.max(prev, 7));
    setCurrentStep(8);
    syncAccountProgress({ preserveCurrentStep: true });
  };
  const onPrevious = () => setCurrentStep((prev) => prev - 1);

  const handleStepClick = (step) => {
    if (!isReviewMode && step > completedStep + 1) return;
    setCurrentStep(step);
  };

  const steps = [
    {
      key: 1,
      title: "Company Info",
      content: (
        <CompanyInfo
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          id={data?.companyInfoId}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 2,
      title: "Directors/Shareholders Info",
      content: (
        <DirectorsShareholdersInfo
          onNext={onNext}
          userId={userId}
          onPrevious={onPrevious}
          devMode={true}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 3,
      title: "Representative Info",
      content: (
        <RepresentativeInfo
          onNext={onNext}
          userId={userId}
          onPrevious={onPrevious}
          id={data?.representativeInfoId}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 4,
      title: "Beneficiary Info",
      content: (
        <BeneficiaryInfo
          onNext={onNext}
          userId={userId}
          onPrevious={onPrevious}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 5,
      title: "Financial Info",
      content: (
        <FinancialInfo
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          id={data?.financialInfoId}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 6,
      title: "Declaration",
      content: (
        <CorporateDeclaration
          onNext={onNext}
          onPrevious={onPrevious}
          userId={userId}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 7,
      title: "Upload Documents",
      content: (
        <UploadDocuments
          onNext={onNext}
          onSubmitted={handleDocumentsSubmitted}
          onPrevious={onPrevious}
          userId={userId}
          id={data?.id}
          isSubmitted={isSubmitted}
          isReviewMode={isFormLocked}
        />
      ),
    },
    {
      key: 8,
      title: "Application Status",
      content: (
        <ApplicationStatus
          isReviewMode={isReviewMode}
          reviewStatus={reviewStatus}
          userId={userId}
          onReviewCompleted={onReviewCompleted}
        />
      ),
    },
  ];

  if (isLoading) return <Loader />;

  return (
    <div className="w-full">
      <div className="overflow-x-auto sticky top-0 py-5 bg-white z-10">
        <div className="min-w-[720px] sm:min-w-0">
          <Steps current={currentStep - 1} size="small">
            {steps.map((step) => (
              <Step
                key={step?.key}
                title={step?.title}
                className="cursor-pointer"
                onClick={() => handleStepClick(step?.key)}
              />
            ))}
          </Steps>
        </div>
      </div>

      <div className="min-h-[400px]">{steps[currentStep - 1]?.content}</div>
    </div>
  );
};

export default OnBoardingSteps;
